import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import GlassCard from "@/v2/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Rocket, Phone, Loader2, Sparkles } from "lucide-react";

interface SprintSubmission {
  step_number: number;
  content: Record<string, any>;
}

interface ADEResult {
  next_hypothesis: string;
  success_metric: string;
  minimum_success_threshold: string;
  why_this_hypothesis: string;
  engine: {
    evidence_quality: string;
    signal_strength: string;
    execution_diagnosis: string;
    next_action: string;
    failure_diagnosis: string | null;
    next_assumption_type: string;
    test_assumption_alignment: string;
  };
}

interface SprintCompletePanelProps {
  submissions: SprintSubmission[];
  finalVerdict?: "continua" | "itera" | "fermati";
}

const VERDICT_CONFIG = {
  continua: { label: "Continua", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  itera:    { label: "Itera",    color: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  fermati:  { label: "Fermati",  color: "bg-red-500/20 text-red-300 border-red-500/40" },
};

export default function SprintCompletePanel({ submissions, finalVerdict }: SprintCompletePanelProps) {
  const navigate = useNavigate();
  const calledRef = useRef(false);
  const [status, setStatus] = useState<"loading" | "done" | "error">("loading");
  const [data, setData] = useState<ADEResult | null>(null);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const byStep = (n: number) => submissions.find((s) => s.step_number === n)?.content ?? {};

    const s1 = byStep(1);
    const s2 = byStep(2);
    const s3 = byStep(3);
    const s4 = byStep(4);
    const s5 = byStep(5);

    const payload = {
      hypothesis:        s1.top_hypothesis ?? s1.hypothesis ?? "",
      assumption_type:   s1.assumption_type ?? undefined,
      success_metric:    s2.success_metric ?? undefined,
      metric_operator:   s2.metric_operator ?? undefined,
      metric_target:     s2.metric_target ?? undefined,
      duration_days:     s2.duration_days ?? undefined,
      daily_logs:        Array.isArray(s3.daily_logs) ? s3.daily_logs : undefined,
      campione:          s4.campione ?? undefined,
      risposte:          s4.risposte ?? undefined,
      conversioni:       s4.conversioni ?? undefined,
      email_lasciate:    s4.email_lasciate ?? undefined,
      sorgenti_traffico: s4.sorgenti_traffico ?? undefined,
      objections:        Array.isArray(s4.objections) ? s4.objections : undefined,
      verdict_signal:    s4.verdict_signal ?? undefined,
      final_verdict:     s5.final_verdict ?? finalVerdict ?? undefined,
      learning:          s5.learning ?? undefined,
      rationale:         s5.rationale ?? undefined,
    };

    supabase.functions
      .invoke("v2-sprint-next-hypothesis", { body: payload })
      .then(({ data: res, error }) => {
        if (error || !res?.next_hypothesis) {
          setStatus("error");
        } else {
          setData(res as ADEResult);
          setStatus("done");
        }
      })
      .catch(() => setStatus("error"));
  }, [submissions, finalVerdict]);

  const verdictCfg =
    finalVerdict && VERDICT_CONFIG[finalVerdict]
      ? VERDICT_CONFIG[finalVerdict]
      : null;

  return (
    <GlassCard className="!p-6 space-y-5 border border-white/10">
      {/* Celebratory block */}
      <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/5 p-5 space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-emerald-300 animate-pulse" />
          <p className="text-sm font-semibold text-emerald-200 uppercase tracking-wider">
            Hai fatto qualcosa che il 99% dei founder non fa mai
          </p>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">
          Hai testato un'ipotesi su persone reali e hai dati concreti. Ora sai qualcosa che prima non sapevi — e questo vale più di qualsiasi business plan.
        </p>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">
              Sprint completato
            </p>
          </div>
          <h2 className="text-xl font-bold">Prossima ipotesi critica</h2>
        </div>
        {verdictCfg && (
          <Badge
            variant="outline"
            className={`shrink-0 text-xs font-semibold px-3 py-1 border ${verdictCfg.color}`}
          >
            {verdictCfg.label}
          </Badge>
        )}
      </div>

      {/* Loading */}
      {status === "loading" && (
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-white/10 rounded w-full" />
          <div className="h-4 bg-white/10 rounded w-4/5" />
          <div className="h-4 bg-white/10 rounded w-3/5" />
          <div className="flex items-center gap-2 text-slate-400 text-sm pt-1">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Elaborazione in corso…
          </div>
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <p className="text-sm text-slate-400">
          Non è stato possibile generare la prossima ipotesi automaticamente. Puoi procedere manualmente rilanciando un nuovo Sprint.
        </p>
      )}

      {/* Done */}
      {status === "done" && data && (
        <div className="space-y-4">
          {/* Hypothesis */}
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <p className="text-sm text-slate-400 mb-1">Ipotesi da testare</p>
            <p className="text-base font-medium leading-relaxed">{data.next_hypothesis}</p>
          </div>

          {/* Metric + threshold */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-xs text-slate-400 mb-1">Metrica di successo</p>
              <p className="text-sm font-medium">{data.success_metric}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-xs text-slate-400 mb-1">Soglia minima</p>
              <p className="text-sm font-medium">{data.minimum_success_threshold}</p>
            </div>
          </div>

          {/* Why */}
          {data.why_this_hypothesis && (
            <p className="text-sm text-slate-300 italic border-l-2 border-primary/40 pl-3">
              {data.why_this_hypothesis}
            </p>
          )}
        </div>
      )}

      {/* CTAs — always visible */}
      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Button
          onClick={() => navigate("/v2/pricing")}
          className="flex-1 bg-primary hover:bg-primary/90 gap-2"
        >
          <Rocket className="w-4 h-4" />
          Lancia un nuovo Sprint
        </Button>
        <Button
          variant="outline"
          className="flex-1 border-border/80 bg-background/50 text-foreground hover:bg-background/80 hover:text-foreground gap-2 font-medium"
          asChild
        >
          <a href="https://cal.com/federico-piccinini" target="_blank" rel="noopener noreferrer">
            <Phone className="w-4 h-4" />
            Prenota una call
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        </Button>
      </div>
    </GlassCard>
  );
}
